// LinkAgentProvider — the persistent owner of the Link (V2) chat's SSE stream +
// session/message state. It calls useLinkAgent ONCE and NEVER unmounts while the
// user is in the app (mounted above LinkLauncherProvider in App.tsx), so opening
// and closing the chat window is irrelevant to the running turn: no re-hydrate,
// no history/liveThread re-stitch, no overlay→kit approval handoff, no session
// re-resolution race. Every surface (the full chat window, the FAB, the "needs
// you" popup) is a pure VIEW over this provider.
//
// The kit's useChatAgent (wrapped by useLinkAgent) does NOT reset its reducer when
// the transport/sessionId changes — it holds state until unmount. So on a SESSION
// SWITCH we call chat.reset(), swap sessionId (which swaps the transport via
// useLinkAgent's useMemo), and set the new history. Closing the chat touches none of
// this → the stream persists.

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { webSessionStore, type ChatEvent } from 'jasit-agent-kit';
import { useChatSession, type ChatSessionRequest, type QueueMode } from 'jasit-agent-kit/react';
import { useLinkAgentSession, type UseLinkAgentSession, type LinkAgentResendPayload } from '../components/dashboard/employer/onboarding/useLinkAgentSession';
import {
    ThreadEntry,
    threadEntriesFromPersistedMessages,
} from '@/components/dashboard/employer/chat/ChatThread';
import {
    linkAgentApi,
    isSessionGoneError,
    type ArtifactShape,
    type ModelOption,
    type LinkAgentReference,
} from '@/lib/api/linkAgentApi';
import { employerChatApi, EmployerChatMessage } from '@/lib/api/employerChatApi';
import { loadDevModelChoice, saveDevModelChoice, decodeArtifactContent } from '@/lib/linkAgentDev';

// ── Message-queue toggle ────────────────────────────────────────────────────
// How messages typed while Link is busy are handled:
//   'batched'    — queue them, then merge into ONE message when Link finishes.
//   'sequential' — queue them, send one at a time after each turn ends.
//   'off'        — classic: you can't send while Link is working (no queue).
// >>> Flip this to 'off' to disable queueing entirely. <<<
// Owned here (the provider owns the useLinkAgent call); the surface imports it for
// its block-while-thinking send guard so the two stay in lockstep.
export const CHAT_QUEUE_MODE = 'batched' as QueueMode;

// Multi-artifact tabs (MAF v1.11.1). Link can emit N artifacts in a single batch
// (emit_artifacts) — each becomes a tab in the preview pane. Exported so the
// LinkAgentSurface view imports the same shape.
export type ArtifactTab = {
    id: string;
    name: string;
    mime: string;
    content: string; // decoded markdown / json text
};

// Artifact shape — pinned to 'markdown' in the UI. The backend still accepts
// 'json' for the zero-translation save path, but the user-facing shape toggle was
// dropped after V2.1. Kept as a typed constant so start reads it from one place.
const SHAPE: ArtifactShape = 'markdown';

// A session-open request handed to openSession(). Replaces the seed/staged/resume
// props that used to remount LinkAgentSurface — now the provider owns them.
// Extends the kit's ChatSessionRequest (which contributes the generic `seed`
// timing field); Nexilink keeps its own names for resume/staged and maps them via
// the useChatSession isResume/hasStaged overrides, plus carries app-specific
// reference + focus fields the kit passes through untouched.
export type OpenSessionRequest = ChatSessionRequest & {
    // Resume an existing chat (re-create the in-process session + hydrate history).
    resumeChatId?: string;
    // Structured references to attach to the seed's first auto-send.
    seedReferences?: LinkAgentReference[];
    // A STAGED (unsent) prompt: the surface pre-fills the composer with it (vs
    // `seed`, which auto-sends). Consumed once via consumeStagedInput().
    stagedInput?: string;
    // References guaranteed-attached to that staged send (merged in handleSend).
    stagedReferences?: LinkAgentReference[];
    // On resume, focus the FIRST artifact tab (the edit target) instead of the
    // most-recently emitted one. Used by the askLink "Fortsätt" path.
    focusFirstArtifact?: boolean;
};

// #48 — session-recovery state after a backend restart wiped the in-process
// session. Drives the FAB's ambient cue (loading → green flash → back to idle,
// or red on failure) and is exposed so any surface can react.
//   'idle'       — nothing to recover.
//   'recovering' — auto-resuming from the persisted transcript (FAB: loading).
//   'restored'   — resume succeeded; brief green flash, then back to 'idle'.
//   'failed'     — the transcript itself couldn't be resumed (FAB: red).
export type LinkRecoveryState = 'idle' | 'recovering' | 'restored' | 'failed';

interface LinkAgentValue {
    sessionId: string | null;
    chat: UseLinkAgentSession;
    history: ThreadEntry[];
    /** #318 — true when the hydrated thread is a WINDOW onto a longer chat, so
     *  the UI can offer to fetch the rest instead of silently showing part of it.
     *  Link's own context is never windowed; this is only about what is drawn. */
    hasOlderHistory: boolean;
    /** Replaces the windowed thread with the FULL transcript. User-initiated —
     *  on a very long chat this is the expensive render we defer by default. */
    loadOlderHistory: () => Promise<void>;
    /**
     * Look once, shortly after a save, for a compaction that R10 folded in the
     * background — and show its divider without making the user reopen the chat.
     *
     * The fold is DETACHED server-side (a save must not wait on an LLM
     * summarisation), so the finalize response cannot report it and no stream
     * event reaches the client. Until this existed, the only way to learn the
     * chat had been summarised was to close and reopen it — and "stay in Link
     * after Spara" removed the very moment that used to happen.
     */
    noteCompactionAfterSave: (chatId: string) => void;
    /** Summary of a fold that happened during THIS session, to be rendered after
     *  the live thread. Null when nothing folded, or once a reopen has placed the
     *  divider at its real boundary instead. */
    trailingCompaction: string | null;
    isLoadingOlder: boolean;
    isInitializing: boolean;
    initError: string | null;
    /** #48 — wiped-session recovery state (drives the FAB cue). */
    recovery: LinkRecoveryState;
    availableModels: ModelOption[];
    currentModel: { provider: string; model: string } | null;
    modelSwitching: boolean;
    // Switch the live session's model mid-chat AND remember the choice for the
    // next chat. THROWS on error so the caller can surface it. Temporary (DEV).
    switchModel: (provider: string, model: string) => Promise<void>;
    artifacts: ArtifactTab[];
    activeArtifactId: string | null;
    updateArtifacts: (next: ArtifactTab[]) => void;
    updateActiveArtifactId: (next: string | null) => void;
    artifactsRef: React.MutableRefObject<ArtifactTab[]>;
    activeArtifactIdRef: React.MutableRefObject<string | null>;
    // Resolve + open a session (resume / fresh / seeded / staged). Bumps
    // requestNonce; an effect performs the resolution + resets the stream first.
    openSession: (req: OpenSessionRequest) => void;
    // The current request's resume target + focus flag (surface reads these where
    // it used to read the equivalent props).
    resumeChatId?: string;
    focusFirstArtifact?: boolean;
    // Consume the STAGED (unsent) prompt + its references exactly once (so a
    // reopen doesn't re-prefill). The surface sets the composer from this.
    consumeStagedInput: () => { input?: string; references?: LinkAgentReference[] } | null;
    // Bumped when Link emits a NEW artifact — the surface expands its preview pane
    // in response (the presentational side-effect that used to be inline).
    newArtifactNonce: number;
    // Bumped once each turn settles — LinkWorkspace refreshes its session list.
    turnEndNonce: number;
    // The id of the last FRESH session created — LinkWorkspace highlights it.
    lastCreatedSessionId: string | null;
    // Bumped on every openSession — the surface can react to a new request.
    requestNonce: number;
    // Bumped to ask the surface to focus its composer (the global ⌘K "open Link"
    // shortcut uses this; the surface also focuses on every open).
    focusComposerNonce: number;
    requestComposerFocus: () => void;
    // Unsent composer draft, persisted across close/reopen (the surface unmounts on
    // close, so the draft lives HERE — the persistent owner — instead of being lost
    // or nagged about with a popup). Cleared on a session switch + on send. The FAB
    // shows a badge when this is non-empty and Link is closed.
    composerDraft: string;
    setComposerDraft: (text: string) => void;
}

// #288 — sessionStorage key holding the id of the session that currently has a
// LIVE turn. Written while streaming so a HARD reload (which destroys the provider
// and its live stream) can re-surface the FAB cue: the launcher reads this on mount
// and validates it via getTurnStatus. Per-tab; survives reload, cleared on a new tab.
export const LINK_ACTIVE_SESSION_KEY = 'nexilink.link.activeSession';

// Persistence via the kit's SessionStore primitive (Proposal 29 piece A) — the
// generic sessionStorage seam (SSR / private-mode / quota guarded), replacing raw
// setItem/getItem/removeItem here AND in the launcher. Nexilink keeps its OWN save
// POLICY (streaming-gated below, for the FAB cue), which is why we drive the store
// directly rather than handing it to useChatSession's save-on-resolve.
export const linkSessionStore = webSessionStore(LINK_ACTIVE_SESSION_KEY);

const LinkAgentContext = createContext<LinkAgentValue | null>(null);

export const useLinkAgent = (): LinkAgentValue => {
    const ctx = useContext(LinkAgentContext);
    if (!ctx) throw new Error('useLinkAgent must be used within a LinkAgentProvider');
    return ctx;
};

export const LinkAgentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [sessionId, setSessionId] = useState<string | null>(null);
    // #48 — a ref mirror so the stable onStreamError callback reads the CURRENT
    // session id (the one that just got wiped) without being re-created each render.
    const sessionIdRef = useRef<string | null>(null);
    sessionIdRef.current = sessionId;

    // #48/#51 — wiped-session recovery now lives in the KIT (useChatSession
    // recoverOnSessionGone, Proposal 34 P2 — v0.31.0). The kit owns the state
    // machine + timing (re-entrancy guard, isInitializing edge, restored→idle
    // flash). We keep only the two app-owned pieces the kit can't know:
    //  • the failed-send payload — so we can replay it (success) or restore it to
    //    the composer (failure); a user's message must NEVER vanish.
    //  • refs the STABLE onError callback needs: it's wired into useLinkAgentSession
    //    (created below), so chat.send + the kit's recover fn are reached via refs
    //    set after those exist.
    // NOTE: the payload here covers only the POST-restart send (which 410s at the
    // ownership check, so the server never persists it). A turn that was STREAMING
    // when the backend died is made durable server-side (HandleSend snapshots the
    // transcript on receipt — go.nexilink link_agent.go / #51), so it simply
    // reappears in the rehydrated history on resume.
    const pendingResendRef = useRef<LinkAgentResendPayload | null>(null);
    const chatSendRef = useRef<UseLinkAgentSession['send'] | null>(null);
    const recoverRef = useRef<
        | ((
              err: unknown,
              resumeReq: OpenSessionRequest,
              hooks?: { onRestored?: () => void; onFailed?: () => void },
          ) => boolean)
        | null
    >(null);

    // #48 — the kit hands us the RAW error from a failed turn stream + (for a send)
    // the message payload. Hand it to the kit's recover fn: it returns false (a
    // no-op) for anything that isn't a wiped session — keeping its normal error
    // surfacing — and otherwise resumes. On a clean resume we replay the failed send
    // (it 410'd before the model saw it, so re-send can't double-process); on a
    // failed resume we drop the text back into the composer. Stable — refs only.
    const onStreamError = useCallback((err: unknown, lastSend?: LinkAgentResendPayload) => {
        pendingResendRef.current = lastSend ?? null;
        recoverRef.current?.(
            err,
            { resumeChatId: sessionIdRef.current ?? undefined },
            {
                onRestored: () => {
                    const p = pendingResendRef.current;
                    pendingResendRef.current = null;
                    if (p?.text) chatSendRef.current?.(p.text, p.attachments, p.displayText, p.references);
                },
                onFailed: () => {
                    const p = pendingResendRef.current;
                    pendingResendRef.current = null;
                    if (p?.text) setComposerDraft(p.text);
                },
            },
        );
    }, []);

    // #288 timing (reattach must fire AFTER React commits the resolved sessionId +
    // its session-bound transport) is now OWNED by the kit: useChatSession runs the
    // integrated reattach in a post-commit effect once `resolve` returns the id. No
    // pendingReattachRef dance here anymore.

    // DEV model-picker state (temporary). availableModels comes from the server
    // (only providers with a configured key are usable); currentModel mirrors the
    // live session; modelSwitching guards the picker during the switch round-trip.
    const [availableModels, setAvailableModels] = useState<ModelOption[]>([]);
    const [currentModel, setCurrentModel] = useState<{ provider: string; model: string } | null>(null);
    const [modelSwitching, setModelSwitching] = useState(false);

    // Persisted/resumed history is Nexilink-rendered (carries plan/ask verdicts +
    // cancelled state the kit doesn't model); the kit owns only the LIVE turn.
    const [history, setHistory] = useState<ThreadEntry[]>([]);
    // #318 — the resume/detail payloads are windowed to the last N exchanges, so
    // remember whether anything was left above and let the user ask for it.
    const [hasOlderHistory, setHasOlderHistory] = useState(false);
    const [isLoadingOlder, setIsLoadingOlder] = useState(false);
    // The messages `history` was built from. Loading older PREPENDS onto this
    // rather than replacing the thread — see loadOlderHistory.
    const hydratedMsgsRef = useRef<EmployerChatMessage[]>([]);
    // How many messages sat ABOVE the window when this chat was hydrated. Fixed
    // at hydration, because it is the only number that stays true as the chat
    // grows — see loadOlderHistory for why deriving it later is wrong.
    const olderCountRef = useRef(0);
    // Where a non-destructive compaction folded this chat, indexed against the
    // FULL transcript. Held in a ref because loadOlderHistory rebuilds the whole
    // thread with a DIFFERENT offset (0, once everything is loaded), and the
    // divider has to be re-placed against that — it is not a one-shot value that
    // can be consumed at hydration and forgotten.
    const compactedThroughRef = useRef(0);
    const compactionSummaryRef = useRef<string | undefined>(undefined);
    // A fold that happened DURING this session, rendered after the live thread —
    // "this just happened", rather than a boundary in the transcript. Reopening
    // the chat re-derives the divider from the persisted pointer and places it at
    // the true boundary instead, so this is cleared on every chat open.
    const [trailingCompaction, setTrailingCompaction] = useState<string | null>(null);
    // How long to wait before looking for a background fold. Comfortably past a
    // typical summarisation (measured 3-4s on gpt-4o and haiku) without being so
    // long that the user has moved on. Not a retry budget — there is exactly one
    // attempt.
    const COMPACTION_RECHECK_MS = 8000;

    // Multi-artifact tabs + their ref mirrors. The SSE generator fires events
    // synchronously back-to-back inside a single async tick — when
    // delete_artifacts emits N StreamArtifactDeleted events, all N fire before
    // React commits state from event 1. Reading from the state closure gives
    // event 2..N a stale list and they overwrite each other. Refs commit
    // synchronously so every event sees the latest list. Paired setters keep ref
    // + state in lockstep.
    const [artifacts, setArtifacts] = useState<ArtifactTab[]>([]);
    const [activeArtifactId, setActiveArtifactId] = useState<string | null>(null);
    const artifactsRef = useRef<ArtifactTab[]>([]);
    const activeArtifactIdRef = useRef<string | null>(null);
    const updateArtifacts = (next: ArtifactTab[]) => {
        artifactsRef.current = next;
        setArtifacts(next);
    };
    const updateActiveArtifactId = (next: string | null) => {
        activeArtifactIdRef.current = next;
        setActiveArtifactId(next);
    };

    // Presentational nonces the surface / workspace react to (they used to be
    // inline side-effects / props before the hoist).
    const [newArtifactNonce, setNewArtifactNonce] = useState(0);
    const [turnEndNonce, setTurnEndNonce] = useState(0);
    const [lastCreatedSessionId, setLastCreatedSessionId] = useState<string | null>(null);
    // Composer-focus request (⌘K shortcut / focus-on-open). Bumping it tells the
    // surface to focus its composer without the provider knowing the textarea.
    const [focusComposerNonce, setFocusComposerNonce] = useState(0);
    const requestComposerFocus = useCallback(() => setFocusComposerNonce((n) => n + 1), []);
    // Unsent composer draft (see interface). Persists across close/reopen; cleared
    // on session switch (the reset callback) + on send.
    const [composerDraft, setComposerDraft] = useState('');

    // The conversation state machine lives in jasit-agent-kit; useLinkAgent adapts
    // it to LinkAgentModal's shape and keeps every Nexilink-specific behaviour (todo
    // dedupe, verdicts, "Avbruten", artifact side-effects) as an overlay. Called
    // ONCE here — the whole point of the hoist.
    const chat = useLinkAgentSession({
        sessionId,
        history,
        // Bump so LinkWorkspace can refresh its session list + auto-title.
        onTurnEnd: () => setTurnEndNonce((n) => n + 1),
        // #48 — a stream failure whose cause is a WIPED session (backend restart)
        // is auto-recoverable: the transcript is persisted, so we transparently
        // resume from it instead of dead-ending on the 400. Any other error keeps
        // its normal error-entry surfacing (the kit already rendered it).
        onError: onStreamError,
        queue: CHAT_QUEUE_MODE,
        onEvent: (ev: ChatEvent) => {
            // Artifact handling stays fully Nexilink-owned (tabs, focus,
            // auto-expand, manual edits); the kit just relays the events. The one
            // presentational line (expand the preview pane) is a nonce bump the
            // surface reacts to, so the pane state stays in the view.
            if (ev.type === 'artifact') {
                const a = ev.artifact;
                const tab: ArtifactTab = { id: a.id, name: a.name, mime: a.mime, content: a.content ?? '' };
                const prev = artifactsRef.current;
                const idx = prev.findIndex((t) => t.id === a.id);
                const isNew = idx < 0;
                updateArtifacts(isNew ? [...prev, tab] : prev.map((t, i) => (i === idx ? tab : t)));
                updateActiveArtifactId(a.id);
                if (isNew) setNewArtifactNonce((n) => n + 1);
            } else if (ev.type === 'artifact_deleted') {
                const prev = artifactsRef.current;
                const next = prev.filter((t) => t.id !== ev.id);
                updateArtifacts(next);
                if (activeArtifactIdRef.current === ev.id) {
                    updateActiveArtifactId(next.length > 0 ? next[next.length - 1].id : null);
                }
            }
        },
    });

    // ── Session request lifecycle ───────────────────────────────────────────
    // The generic orchestration — request-nonce, reset-BEFORE-resolve (the kit
    // reducer doesn't reset on a transport swap), fire seed/reattach once ready,
    // staged consume-once, isInitializing/initError — lives in the kit's
    // useChatSession (the "background mode" primitive, extracted from this file).
    // We supply only the Nexilink-specific behaviour: what "reset" clears, how a
    // session is opened (start vs resume + hydrate history/artefacts/models), how a
    // seed is sent (with references), and how reattach happens. See
    // go.jasit.agent/BACKGROUND_MODE_DESIGN.md.
    const session = useChatSession<OpenSessionRequest>({
        // Clear the kit's live turn + all Nexilink-derived state for a new session.
        reset: () => {
            chat.reset();
            updateArtifacts([]);
            updateActiveArtifactId(null);
            setHistory([]);
            // #318 — the window flags belong to the chat being LEFT. Without this
            // a fresh chat inherits "there is older history" from the long chat you
            // just closed, and offers to load a past that this chat never had.
            setHasOlderHistory(false);
            hydratedMsgsRef.current = [];
            olderCountRef.current = 0;
            setSessionId(null);
            // A different session gets a fresh composer — the previous draft
            // belonged to the session we're leaving.
            setComposerDraft('');
        },
        // Nexilink resume/staged use their own field names.
        isResume: (req) => !!req.resumeChatId,
        hasStaged: (req) => !!req.stagedInput,
        // Open the backend session: start fresh or resume + hydrate. RETURNS the
        // resolved session id — which opts this session into the kit's integrated
        // reattach (getTurnStatus/onReplay/presentGate below) with the post-commit
        // timing baked in. Throws (with a Swedish message) to surface initError.
        resolve: async (req): Promise<string> => {
            if (req.resumeChatId) {
                const rid = req.resumeChatId;
                const focusFirst = req.focusFirstArtifact;
                // Resume path — server re-create + local message hydrate + live-
                // artifacts fetch in parallel. All three are needed before the
                // surface becomes interactive: session_id (so /messages calls have a
                // target), the thread (so the user sees history), and the preview
                // tabs (repopulated for any artifacts Link previously emitted).
                let info, detail, live;
                try {
                    [info, detail, live] = await Promise.all([
                        linkAgentApi.resume(rid),
                        employerChatApi.get(rid),
                        linkAgentApi.liveArtifacts(rid).catch((err) => {
                            // Artifact rehydration is nice-to-have; never fail the
                            // resume on it. Preview stays empty and the user continues.
                            console.warn('liveArtifacts fetch failed:', err);
                            return { artifacts: [] };
                        }),
                    ]);
                } catch (err: any) {
                    throw new Error(err?.message || 'Kunde inte återuppta chatten.');
                }
                setSessionId(info.session_id);
                setAvailableModels(info.available_models ?? []);
                setCurrentModel({ provider: info.provider, model: info.model });
                // #288 — hydrate from the LIVE in-memory conversation
                // (Session.Messages()) whenever it's present, because it is the true
                // current state: the engine commits the assistant message (any text
                // written BEFORE a pause AND the dangling tool_call) to Messages() at
                // the pause, so mid-turn it already holds the just-sent prompt +
                // streamed story + pending call. We hydrate the FULL live list — no
                // trimming — and let useLinkAgent reconcile the single dangling pending
                // call away at the thread seam. Prefer the longer of live/persisted to
                // stay safe if a rehydrated live session is ever shorter than disk.
                const persisted = detail.messages ?? [];
                const liveMsgs = info.messages ?? [];
                const hydrateFrom = liveMsgs.length >= persisted.length ? liveMsgs : persisted;
                hydratedMsgsRef.current = hydrateFrom;
                olderCountRef.current = Math.max(
                    0,
                    (info.total_messages ?? detail.total_messages ?? hydrateFrom.length) - hydrateFrom.length,
                );
                // Caveat: the fold index is measured against the PERSISTED
                // transcript, while hydrateFrom may be the LIVE one when a
                // resumed session is longer than disk. They are the same
                // conversation and the fold sits far behind the divergence, so
                // the worst case is the divider landing a message or two off —
                // not worth reconciling two indexes to fix, but worth knowing
                // before someone reports it as an off-by-one.
                compactedThroughRef.current = detail.compacted_through ?? 0;
                compactionSummaryRef.current = detail.compaction_summary;
                // The persisted pointer now places the divider at its true
                // boundary, so drop the session-local "this just happened" one.
                setTrailingCompaction(null);
                setHistory(
                    threadEntriesFromPersistedMessages(hydrateFrom, {
                        through: compactedThroughRef.current,
                        summary: compactionSummaryRef.current,
                        offset: olderCountRef.current,
                    }),
                );
                // #318 — both payloads are windowed identically, so either flag is
                // authoritative; OR them so a missing field on one path can't hide
                // the fact that older messages exist.
                setHasOlderHistory(Boolean(info.has_more) || Boolean(detail.has_more));
                // #26 — re-show the ORIGINAL suggestion the user left this chat on.
                // Seeded AFTER the reset-before-resolve, so it sticks; a live turn's
                // reattach (if any) streams a fresh suggest_next that supersedes it.
                if (info.suggestion) chat.seedSuggestion(info.suggestion);
                if (live.artifacts.length > 0) {
                    const tabs: ArtifactTab[] = live.artifacts.map((a) => ({
                        id: a.id,
                        name: a.name,
                        mime: a.mime,
                        content: decodeArtifactContent(a.content_b64),
                    }));
                    updateArtifacts(tabs);
                    // Default: focus the latest tab (last in emission order),
                    // mirroring the live `artifact` event handler. For the askLink
                    // "Fortsätt" path, focus the FIRST tab — the entity the chat
                    // started editing (the one the user clicked).
                    updateActiveArtifactId((focusFirst ? tabs[0] : tabs[tabs.length - 1]).id);
                }
                return info.session_id;
            }
            // Fresh start — DEV: use the remembered provider/model (between-chat).
            const devChoice = loadDevModelChoice();
            let info;
            try {
                info = await linkAgentApi.start(undefined, SHAPE, devChoice?.provider, devChoice?.model);
            } catch (err: any) {
                throw new Error(err?.message || 'Kunde inte starta AI-sessionen.');
            }
            setSessionId(info.session_id);
            setAvailableModels(info.available_models ?? []);
            setCurrentModel({ provider: info.provider, model: info.model });
            setLastCreatedSessionId(info.session_id);
            return info.session_id;
        },
        // Seed autosend (fresh + seeded). The kit echoes the user message + drives
        // the turn; pass structured references so the seeded entity is injected as
        // context server-side (same channel as a composer @-mention).
        seedSend: (req) => {
            chat.send(
                req.seed!,
                undefined,
                undefined,
                req.seedReferences && req.seedReferences.length > 0 ? req.seedReferences : undefined,
            );
        },
        // Integrated reattach (resume). The kit runs decideReattach in a POST-COMMIT
        // effect (transport live) — no pendingReattachRef dance. We supply the three
        // Nexilink primitives it dispatches to:
        //  • getTurnStatus → the run-state (running / paused-with-gate / idle),
        //  • onReplay → tail a still-running turn (kit reattachRunning),
        //  • presentGate → re-present a paused turn's gate as the #298 overlay.
        //  • isBusy → skip when a live stream is already feeding this provider
        //    (reopen-without-reload), so a second reattach can't race the first.
        getTurnStatus: chat.getTurnStatus,
        onReplay: chat.reattachRunning,
        presentGate: chat.presentReattachGate,
        isBusy: () => chat.isStreaming,
        // #48/#51 — opt into the KIT's wiped-session recovery (Proposal 34 P2,
        // v0.31.0). onStreamError / switchModel call session.recoverFromSessionGone;
        // the kit owns the resume + the recovery state machine + timing. Detector =
        // Nexilink's isSessionGoneError (handles 410 + AI_SESSION_GONE + the legacy
        // 400 "not found" shape); the kit default would only catch 410/code.
        recoverOnSessionGone: true,
        isSessionGone: isSessionGoneError,
    });
    const { openSession, requestNonce, isInitializing, initError, request, consumeStaged } = session;

    // #48/#51 — wire the refs the STABLE onStreamError callback reaches through:
    // chat.send (to replay a recovered message) + the kit's recover fn. Both targets
    // exist now (chat + session created above); the callback was defined before them.
    chatSendRef.current = chat.send;
    recoverRef.current = session.recoverFromSessionGone;

    // #288 — remember the session that has a live turn so a HARD reload can bring
    // the FAB cue (glow/spin) back before Link is even reopened. The launcher reads
    // this on mount and validates it via getTurnStatus. We persist while streaming
    // (a paused turn was streaming first, so its id is captured too) via the kit's
    // SessionStore primitive (private-mode / SSR safe).
    useEffect(() => {
        if (sessionId && chat.isStreaming) linkSessionStore.save(sessionId);
    }, [sessionId, chat.isStreaming]);

    // consumeStagedInput — Nexilink shape over the kit's one-shot consumeStaged:
    // pull the STAGED (unsent) prompt + its references exactly once so a reopen
    // doesn't re-prefill the composer.
    const consumeStagedInput = useCallback(() => {
        const req = consumeStaged();
        if (!req || !req.stagedInput) return null;
        return { input: req.stagedInput, references: req.stagedReferences };
    }, [consumeStaged]);

    // DEV model-picker: switch the live session's model mid-chat AND remember the
    // choice for the next chat. THROWS on error so the caller can surface it.
    const switchModel = useCallback(async (provider: string, model: string) => {
        saveDevModelChoice(provider, model);
        if (!sessionId) {
            // No live session yet — remember; it applies on the next start.
            setCurrentModel({ provider, model });
            return;
        }
        setModelSwitching(true);
        try {
            const info = await linkAgentApi.setModel(sessionId, provider, model);
            setCurrentModel({ provider: info.provider, model: info.model });
            if (info.available_models) setAvailableModels(info.available_models);
        } catch (err) {
            // #48 — a model switch on a WIPED session (server restart) 400s/410s.
            // Don't surface the scary raw "session not found" — let the kit recover
            // transparently (the choice is already saved, so it re-applies on the
            // resumed session). recoverFromSessionGone returns false for a real error.
            if (recoverRef.current?.(err, { resumeChatId: sessionId })) return;
            throw err; // a real error — let the caller surface it
        } finally {
            setModelSwitching(false);
        }
    }, [sessionId]);

    // #318 — replace the windowed thread with the FULL transcript, on demand.
    // turns=0 asks the backend for everything; this is the render we deliberately
    // do not do on open, so it stays behind a click the user chose to make.
    //
    // Reads from the PERSISTED snapshot rather than the live session: everything
    // above the window is committed history by definition, and the live tail is
    // already on screen. If the fetch fails the existing thread is left exactly as
    // it is — a failed "show me more" must never take away what was already shown.
    const loadOlderHistory = useCallback(async () => {
        const id = sessionIdRef.current;
        // Only meaningful for a thread that was actually WINDOWED. Guarded here as
        // well as in the UI so a stale flag can never turn this into "re-hydrate
        // the thread from disk", which is a different and destructive operation.
        if (!id || !hasOlderHistory) return;
        setIsLoadingOlder(true);
        try {
            const detail = await employerChatApi.get(id, 0);
            const full = detail.messages ?? [];
            // PREPEND the part above the window; never replace the thread.
            //
            // `history` is only the hydrated SEED — the live session layers its own
            // entries on top, and every finished turn is also in this snapshot. So
            // re-seeding from the full list renders those turns twice, once from
            // each source. Taking just the prefix leaves everything already on
            // screen exactly where it is.
            const shown = hydratedMsgsRef.current;
            // Sliced by the count captured AT HYDRATION, never by
            // full.length - shown.length. The chat keeps growing: every finished
            // turn appends to this same snapshot, so the derived figure drifts by
            // exactly the number of new messages and the slice reaches into the
            // window — re-showing its oldest turns a second time. That is a
            // duplication you cannot see by eye, because it lands hundreds of
            // messages up. The messages above the window never change, so the
            // count taken when the window was drawn stays correct forever.
            const prefix = full.slice(0, Math.min(olderCountRef.current, full.length));
            if (prefix.length === 0) {
                setHasOlderHistory(false);
                return;
            }
            const merged = [...prefix, ...shown];
            hydratedMsgsRef.current = merged;
            olderCountRef.current = 0;
            // Offset is now 0 — the whole transcript is loaded — so the fold
            // index applies directly. This is the case where the divider becomes
            // visible for a chat that was compacted ABOVE its initial window.
            setHistory(
                threadEntriesFromPersistedMessages(merged, {
                    through: compactedThroughRef.current,
                    summary: compactionSummaryRef.current,
                    offset: 0,
                }),
            );
            setHasOlderHistory(false);
        } catch (err) {
            console.warn('kunde inte ladda äldre meddelanden', err);
        } finally {
            setIsLoadingOlder(false);
        }
    }, [hasOlderHistory]);

    // R10 — see noteCompactionAfterSave in the context type for why this exists.
    //
    // ONE look, not a poll. The fold takes a few seconds (it is an LLM call), so
    // this waits, asks once, and gives up. A miss costs nothing: the divider is
    // persisted, so it appears the next time the chat is opened. Retrying would
    // buy a rare divider at the price of a request loop running behind every
    // save.
    //
    // The divider is APPENDED rather than placed at the fold boundary, because
    // the live thread is a stream of events with no message index to seek to.
    // That is honest: the copy reads "Link sammanfattade äldre historik", a
    // statement about something that just happened, not a claim that everything
    // above the line was folded. Reopening re-derives it from the persisted
    // pointer and puts it at the true boundary.
    const noteCompactionAfterSave = useCallback((chatId: string) => {
        window.setTimeout(async () => {
            try {
                const detail = await employerChatApi.get(chatId);
                const through = detail.compacted_through ?? 0;
                if (through <= compactedThroughRef.current) return; // nothing new folded
                compactedThroughRef.current = through;
                compactionSummaryRef.current = detail.compaction_summary;
                // NOT setHistory. The thread renders as [...history, ...liveThread],
                // so appending to history puts the divider BEFORE every live
                // message — and in a fresh chat, where history is empty and
                // everything is live, it becomes the very first entry: a line
                // saying older history was summarised, with nothing above it.
                // This is a trailing slot the modal renders after the live thread.
                setTrailingCompaction(detail.compaction_summary ?? '');
            } catch {
                /* Non-fatal by design — the divider is persisted and shows on reopen. */
            }
        }, COMPACTION_RECHECK_MS);
    }, []);

    return (
        <LinkAgentContext.Provider
            value={{
                sessionId,
                chat,
                history,
                hasOlderHistory,
                loadOlderHistory,
                noteCompactionAfterSave,
                trailingCompaction,
                isLoadingOlder,
                isInitializing,
                initError,
                recovery: session.recovery,
                availableModels,
                currentModel,
                modelSwitching,
                switchModel,
                artifacts,
                activeArtifactId,
                updateArtifacts,
                updateActiveArtifactId,
                artifactsRef,
                activeArtifactIdRef,
                openSession,
                resumeChatId: request?.resumeChatId,
                focusFirstArtifact: request?.focusFirstArtifact,
                consumeStagedInput,
                newArtifactNonce,
                turnEndNonce,
                lastCreatedSessionId,
                requestNonce,
                focusComposerNonce,
                requestComposerFocus,
                composerDraft,
                setComposerDraft,
            }}
        >
            {children}
        </LinkAgentContext.Provider>
    );
};
